import React, { useState, useEffect, useCallback } from 'react';
import { createApiClient } from '../lib/apiClient';
import DashboardLayout from '../components/DashboardLayout';
import TaxPhase3Tab from '../components/TaxPhase3Tab';
import TaxReimbursementsTab from '../components/TaxReimbursementsTab';
import TaxImportTab from '../components/TaxImportTab';
import TaxProfitTab from '../components/TaxProfitTab';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Receipt, Plus, Trash2, CheckCircle2, Loader2, Lock, Unlock, X, AlertTriangle, Info, MessageSquare, Smartphone, DollarSign, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = createApiClient();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BLUE = '#002FA7';

const Card = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`tax-card-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</div>
    <div className={`text-xl font-bold mt-1 ${accent || 'text-slate-900'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</div>
  </div>
);

const Badge = ({ status }) => {
  const map = {
    Approved: 'bg-green-50 text-green-700 border-green-200',
    'Needs Review': 'bg-amber-50 text-amber-700 border-amber-200',
    Uncategorized: 'bg-slate-100 text-slate-600 border-slate-200',
    'Missing Receipt': 'bg-orange-50 text-orange-700 border-orange-200',
    'Personal/Non-Business': 'bg-purple-50 text-purple-700 border-purple-200',
    Locked: 'bg-slate-200 text-slate-700 border-slate-300',
  };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[status] || map.Uncategorized}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{status}</span>;
};

const Disclaimer = ({ text }) => (
  <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {text}
  </div>
);

const TaxesExpenses = ({ initialTab = 'dashboard' }) => {
  const { user } = useAuth();
  const canNote = ['company_admin', 'super_admin', 'accountant'].includes(user?.role);
  const isAdmin = ['company_admin', 'super_admin'].includes(user?.role);
  const [tab, setTab] = useState(initialTab);
  // Keep the active tab in sync when navigating between sidebar sub-items
  // (React reuses this component instance, so useState alone won't update).
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dash, setDash] = useState(null);
  const [expenses, setExpenses] = useState({ items: [], total: 0 });
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [report, setReport] = useState(null);
  const [audit, setAudit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState('');
  const [notesFor, setNotesFor] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);

  const yearStatus = dash?.year_status || years.find((y) => y.year === year)?.status || 'open';
  const locked = yearStatus === 'closed';

  const loadYears = useCallback(async () => {
    try { const r = await ax.get('/tax/years'); setYears(r.data); if (r.data.length && !r.data.find((y) => y.year === year)) setYear(r.data[0].year); } catch (e) {}
  }, [year]);

  const loadDash = useCallback(async () => {
    try { const r = await ax.get(`/tax/dashboard?year=${year}`); setDash(r.data); } catch (e) {}
  }, [year]);
  const loadExpenses = useCallback(async () => {
    try { const r = await ax.get(`/tax/expenses?year=${year}&page_size=100`); setExpenses(r.data); } catch (e) {}
  }, [year]);
  const loadCats = useCallback(async () => {
    try { const r = await ax.get('/tax/categories'); setCategories(r.data.categories); } catch (e) {}
  }, []);
  const loadSettings = useCallback(async () => {
    try { const r = await ax.get('/tax/settings'); setSettings(r.data); } catch (e) {}
  }, []);

  useEffect(() => { (async () => { setLoading(true); await loadYears(); await loadCats(); await loadSettings(); setLoading(false); })(); }, [loadYears, loadCats, loadSettings]);
  useEffect(() => { loadDash(); loadExpenses(); }, [year, loadDash, loadExpenses]);
  useEffect(() => { if (tab === 'reports') ax.get(`/tax/report/summary?year=${year}`).then((r) => setReport(r.data)).catch(() => {}); if (tab === 'audit') ax.get(`/tax/audit-check?year=${year}`).then((r) => setAudit(r.data)).catch(() => {}); }, [tab, year]);

  const uploadReceipt = async (fileList) => {
    const file = fileList?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('year', year);
      const r = await ax.post('/tax/expenses/upload', fd);
      flash(`Receipt processed (OCR ${r.data.ocr_confidence || 0}% confidence) — review the extracted expense.`);
      loadExpenses(); loadDash();
    } catch (e) { flash(e.response?.data?.detail || 'Upload failed.'); } finally { setUploading(false); }
  };
  const copyReport = () => {
    if (!report) return;
    const txt = report.categories.map((c) => `${c.category}: $${c.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`).join('\n')
      + `\nTOTAL: $${report.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    navigator.clipboard?.writeText(txt); flash('Tax summary copied to clipboard.');
  };
  const exportReport = (fmt) => window.open(`${API}/tax/report/export?year=${year}&fmt=${fmt}`, '_blank');

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const approve = async (id) => { await ax.post(`/tax/expenses/${id}/approve`); loadExpenses(); loadDash(); };
  const remove = async (id) => { if (!window.confirm('Archive this expense?')) return; await ax.delete(`/tax/expenses/${id}`); loadExpenses(); loadDash(); };
  const openNotes = async (exp) => {
    setNotesFor(exp); setNotes([]); setNoteText(''); setNotesLoading(true);
    try { const r = await ax.get(`/tax/expenses/${exp.id}/notes`); setNotes(r.data); } catch (e) {} finally { setNotesLoading(false); }
  };
  const addNote = async () => {
    const t = noteText.trim(); if (!t || !notesFor) return;
    try { const r = await ax.post(`/tax/expenses/${notesFor.id}/notes`, { text: t }); setNotes((n) => [r.data, ...n]); setNoteText(''); }
    catch (e) { flash(e.response?.data?.detail || 'Could not add note.'); }
  };
  const toggleReimburse = async (e) => {
    const next = !(e.reimbursement_status === 'pending');
    try {
      await ax.post(`/tax/expenses/${e.id}/reimbursable`, { reimbursable: next });
      flash(next ? `Marked reimbursable to ${e.driver_name || 'driver'}.` : 'Removed from reimbursements.');
      loadExpenses();
    } catch (err) { flash(err.response?.data?.detail || 'Could not update reimbursement.'); }
  };
  const closeYear = async () => {
    const r = await ax.post(`/tax/years/${year}/close`); const i = r.data.issues_at_close || {};
    flash(`Year ${year} closed. Open items at close — review:${i.needs_review} missing receipts:${i.missing_receipt} dupes:${i.possible_duplicate} uncategorized:${i.uncategorized}`);
    loadYears(); loadDash();
  };
  const reopenYear = async () => {
    const reason = window.prompt('Reason for reopening this tax year?'); if (!reason) return;
    await ax.post(`/tax/years/${year}/reopen`, { reason }); flash(`Year ${year} reopened.`); loadYears(); loadDash();
  };

  const s = dash?.summary || {};
  const cards = [
    ['Total Revenue', money(s.total_revenue), 'text-green-700'],
    ['Total Expenses', money(s.total_expenses), 'text-red-700'],
    ['Net Business Income', money(s.net_business_income), (s.net_business_income >= 0 ? 'text-green-700' : 'text-red-700')],
    ['Cost / Mile', s.cost_per_mile ? `$${s.cost_per_mile}` : '—'],
    ['Fuel', money(s.fuel)], ['Repairs & Maintenance', money(s.repairs_maintenance)],
    ['Insurance', money(s.insurance)], ['Driver Payroll', money(s.wages)],
    ['Contractor Payments', money(s.contract_labor)], ['Truck/Trailer Lease', money(s.rent_lease)],
    ['Tolls & Other', money(s.other)], ['Taxes & Permits', money(s.taxes_licenses)],
    ['Interest', money(s.interest)], ['Office', money(s.office)],
    ['Missing Receipts', s.missing_receipt ?? 0, 'text-orange-600'],
    ['Needs Review', s.needs_review ?? 0, 'text-amber-600'],
    ['Uncategorized', s.uncategorized ?? 0, 'text-slate-600'],
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="taxes-expenses-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Receipt className="w-6 h-6" style={{ color: BLUE }} />
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Taxes &amp; Expenses</h1>
              {locked && <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-300 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" /> {year} Locked</span>}
            </div>
            <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trucking expense management &amp; tax-preparer-ready reports.</p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="tax-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm font-semibold focus:ring-2 focus:ring-[#002FA7] focus:outline-none" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {Array.from(new Set([...(years.map((y) => y.year)), new Date().getFullYear(), year])).sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {toast && <div data-testid="tax-toast" className="mb-4 px-4 py-2.5 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{toast}</div>}

        <div className="flex gap-1 border-b border-slate-200 mb-6">
          {[['dashboard', 'Dashboard'], ['expenses', 'Expenses'], ['import', 'Import'], ['reimburse', 'Reimbursements'], ['profit', 'Trip Profit'], ['assets', 'Assets & Analysis'], ['audit', 'Audit Check'], ['reports', 'Tax Preparer'], ['settings', 'Tax Settings']].map(([k, l]) => (
            <button key={k} data-testid={`tax-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${tab === k ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{l}</button>
          ))}
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div> : (
          <>
            {tab === 'dashboard' && dash && (
              <div data-testid="tax-dashboard">
                <Disclaimer text={dash.disclaimer} />
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                  {cards.map(([l, v, a]) => <Card key={l} label={l} value={v} accent={a} />)}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white border border-slate-200 rounded-md p-4">
                    <h3 className="text-sm font-bold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Expenses by Month</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={dash.charts.by_month.map((m) => ({ name: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m.month - 1], amount: m.amount }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={50} />
                        <Tooltip formatter={(v) => money(v)} /><Bar dataKey="amount" fill={BLUE} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-md p-4">
                    <h3 className="text-sm font-bold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Top Tax Categories</h3>
                    <div className="space-y-2">
                      {dash.charts.by_tax_category.slice(0, 8).map((c) => {
                        const max = dash.charts.by_tax_category[0]?.amount || 1;
                        return (
                          <div key={c.name}>
                            <div className="flex justify-between text-xs mb-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}><span className="text-slate-600">{c.name}</span><span className="font-semibold text-slate-900">{money(c.amount)}</span></div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.amount / max) * 100}%`, background: BLUE }} /></div>
                          </div>
                        );
                      })}
                      {dash.charts.by_tax_category.length === 0 && <p className="text-sm text-slate-400">No expenses yet for {year}.</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'expenses' && (
              <div data-testid="tax-expenses-tab">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{expenses.total} expense(s) in {year}</p>
                  <div className="flex items-center gap-2">
                    <label data-testid="upload-receipt-button" className={`flex items-center gap-2 border border-[#002FA7] text-[#002FA7] px-4 py-2 rounded-md text-sm font-semibold cursor-pointer hover:bg-[#002FA7] hover:text-white transition-all ${locked || uploading ? 'opacity-40 pointer-events-none' : ''}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Upload Receipt (AI)
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => uploadReceipt(e.target.files)} />
                    </label>
                    <button data-testid="add-expense-button" disabled={locked} onClick={() => setShowAdd(true)}
                      className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <Plus className="w-4 h-4" /> Add Expense
                    </button>
                  </div>
                </div>
                {expenses.items.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-slate-200 rounded-md" data-testid="tax-empty-state">
                    <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No expenses have been recorded for {year}.</p>
                    {!locked && <button onClick={() => setShowAdd(true)} className="mt-3 text-[#002FA7] font-semibold text-sm hover:underline">Add your first expense</button>}
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
                    <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-400">
                        <tr>{['Date', 'Vendor', 'Category', 'Amount', 'Deductible', 'Receipt', 'Status', ''].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {expenses.items.map((e) => (
                          <tr key={e.id} className="hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                            <td className="px-3 py-2 text-slate-700">{e.expense_date}</td>
                            <td className="px-3 py-2 text-slate-900 font-medium">
                              <span className="inline-flex items-center gap-1.5">
                                {e.vendor_name || '—'}
                                {e.source_type === 'driver_mobile' && (
                                  <span title={`Submitted by driver${e.driver_name ? ` · ${e.driver_name}` : ''}`} data-testid={`driver-source-${e.id}`}
                                    className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#002FA7] bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">
                                    <Smartphone className="w-3 h-3" /> Driver
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-slate-600">{e.category_name || '—'}</td>
                            <td className="px-3 py-2 text-slate-900 font-semibold">{money(e.amount)}</td>
                            <td className="px-3 py-2 text-slate-600">{money(e.deductible_amount)}</td>
                            <td className="px-3 py-2">{e.receipt_status === 'Missing Receipt' ? <span className="text-orange-600 text-xs">Missing</span> : <span className="text-green-600 text-xs">Yes</span>}</td>
                            <td className="px-3 py-2"><Badge status={e.review_status} /></td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button data-testid={`notes-${e.id}`} onClick={() => openNotes(e)} title="Review notes" className="text-slate-400 hover:text-[#002FA7] mr-2"><MessageSquare className="w-4 h-4 inline" /></button>
                              {canNote && e.driver_id && e.reimbursement_status !== 'paid' && (
                                <button data-testid={`reimburse-${e.id}`} onClick={() => toggleReimburse(e)} title={e.reimbursement_status === 'pending' ? 'Reimbursable (click to remove)' : 'Mark reimbursable to driver'}
                                  className={`mr-2 ${e.reimbursement_status === 'pending' ? 'text-green-600' : 'text-slate-400 hover:text-green-600'}`}><DollarSign className="w-4 h-4 inline" /></button>
                              )}
                              {e.reimbursement_status === 'paid' && <span className="text-[10px] text-green-600 font-semibold mr-2" title="Reimbursed on payroll">Reimbursed</span>}
                              {e.review_status !== 'Approved' && !locked && <button data-testid={`approve-${e.id}`} onClick={() => approve(e.id)} title="Approve" className="text-green-600 hover:text-green-800 mr-2"><CheckCircle2 className="w-4 h-4 inline" /></button>}
                              {!locked && <button data-testid={`delete-${e.id}`} onClick={() => remove(e.id)} title="Archive" className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 'assets' && <TaxPhase3Tab year={year} locked={locked} />}

            {tab === 'import' && <TaxImportTab year={year} locked={locked} isAdmin={isAdmin} />}

            {tab === 'reimburse' && <TaxReimbursementsTab isAdmin={isAdmin} />}

            {tab === 'profit' && <TaxProfitTab isAdmin={isAdmin} />}

            {tab === 'audit' && audit && (
              <div data-testid="tax-audit-tab">
                <div className="bg-white border border-slate-200 rounded-md p-6 mb-4 flex items-center gap-6">
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${audit.readiness >= 90 ? 'text-green-600' : audit.readiness >= 60 ? 'text-amber-600' : 'text-red-600'}`} style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="audit-readiness">{audit.readiness}%</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tax Prep Readiness</div>
                  </div>
                  <div className="flex-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <p className="text-sm text-slate-700">{audit.approved} of {audit.total_expenses} expenses approved · {audit.open_blocking_issues} blocking issue(s).</p>
                    <p className="text-xs text-slate-400 mt-1">Internal data-quality check — not an IRS audit.</p>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                  {audit.checks.map((c) => (
                    <div key={c.key} className="flex items-center justify-between px-4 py-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid={`audit-${c.key}`}>
                      <span className="text-sm text-slate-700">{c.label}</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${c.count === 0 ? 'bg-green-50 text-green-700' : c.severity === 'high' ? 'bg-red-50 text-red-700' : c.severity === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{c.count === 0 ? '✓ Clear' : c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'reports' && (
              <div data-testid="tax-reports-tab">
                <Disclaimer text={report?.disclaimer || 'These values are based on the expense classifications and tax mappings configured in CivilX TMS. Review with your tax professional before filing.'} />
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Tax Preparer Summary — {year}</h3>
                    {report?.business && <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{report.business.legal_name || '—'} · {report.business.entity_type || '—'} · {report.business.tax_form || '—'} · EIN {report.business.ein_masked || '—'}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid="copy-report" onClick={copyReport} className="border border-slate-300 text-slate-700 px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-50">Copy All</button>
                    <button data-testid="export-csv" onClick={() => exportReport('csv')} className="border border-slate-300 text-slate-700 px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-50">Export CSV</button>
                    <button data-testid="export-pdf" onClick={() => exportReport('pdf')} className="bg-[#002FA7] text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D]">Download PDF</button>
                    <button data-testid="year-package" onClick={() => window.open(`${API}/tax/report/year-package?year=${year}`, '_blank')} className="flex items-center gap-1.5 bg-slate-800 text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-900" title="Zip: summary PDF/CSV + every receipt"><Package className="w-4 h-4" /> Year-End Package (ZIP)</button>
                  </div>
                </div>
                {report && (
                  <div className="bg-white border border-slate-200 rounded-md overflow-hidden" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {report.categories.length === 0 ? <p className="text-sm text-slate-400 p-6">No expenses recorded for {year}.</p> : report.categories.map((c) => (
                      <details key={c.category} className="border-b border-slate-100" data-testid={`report-cat-${c.category.replace(/\s+/g, '-')}`}>
                        <summary className="flex justify-between items-center px-4 py-3 cursor-pointer hover:bg-slate-50">
                          <span className="text-sm font-semibold text-slate-800">{c.category}</span>
                          <span className="text-sm font-bold text-slate-900">{money(c.amount)}</span>
                        </summary>
                        <div className="bg-slate-50 px-6 py-2">
                          {c.subcategories.map((s) => (
                            <div key={s.name} className="flex justify-between text-xs py-1 text-slate-600"><span>{s.name}</span><span>{money(s.amount)}</span></div>
                          ))}
                        </div>
                      </details>
                    ))}
                    <div className="flex justify-between items-center px-4 py-3 bg-slate-800 text-white">
                      <span className="text-sm font-bold">TOTAL EXPENSES</span><span className="text-sm font-bold">{money(report.total)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'settings' && settings && (
              <div data-testid="tax-settings-tab" className="max-w-2xl space-y-6">
                <Disclaimer text={settings.disclaimer} />
                {!isAdmin && (
                  <div data-testid="settings-readonly-note" className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> You have accountant (read-only) access. You can review and export everything, but financial settings and year locks are managed by an admin.
                  </div>
                )}
                <SettingsForm settings={settings} readOnly={!isAdmin} onSaved={(d) => { setSettings(d); flash('Tax settings saved.'); }} />
                {isAdmin && (
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Tax Year {year}</h3>
                  <p className="text-xs text-slate-500 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status: <span className="font-semibold">{yearStatus}</span>. Locking prevents edits until reopened.</p>
                  {locked
                    ? <button data-testid="reopen-year" onClick={reopenYear} className="flex items-center gap-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-50"><Unlock className="w-4 h-4" /> Reopen {year}</button>
                    : <button data-testid="close-year" onClick={closeYear} className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-900"><Lock className="w-4 h-4" /> Close Tax Year {year}</button>}
                </div>
                )}
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <h3 className="text-sm font-bold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Expense Categories ({categories.length})</h3>
                  <p className="text-xs text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Default trucking categories mapped to configurable tax-reporting buckets.</p>
                  <div className="max-h-64 overflow-y-auto border border-slate-100 rounded">
                    {categories.slice(0, 60).map((c) => (
                      <div key={c.id} className="flex justify-between text-xs px-3 py-1.5 border-b border-slate-50" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className="text-slate-700">{c.name}</span>
                        <span className="text-slate-400">{c.tax_report_category} · {c.default_deductible_pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showAdd && <AddExpenseModal year={year} categories={categories} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadExpenses(); loadDash(); flash('Expense added.'); }} />}

      {notesFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="notes-modal" onClick={() => setNotesFor(null)}>
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div>
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Review Notes</h3>
                <p className="text-xs text-slate-500">{notesFor.vendor_name || 'Expense'} · {money(notesFor.amount)} · {notesFor.expense_date}</p>
              </div>
              <button data-testid="notes-close" onClick={() => setNotesFor(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-3 max-h-72 overflow-y-auto space-y-3">
              {notesLoading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" /></div>
                : notes.length === 0 ? <p className="text-xs text-slate-400 text-center py-6">No review notes yet.</p>
                : notes.map((n) => (
                  <div key={n.id} className="border border-slate-100 rounded-md px-3 py-2 bg-slate-50" data-testid="review-note">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{n.author} <span className="uppercase">({n.author_role})</span> · {n.at ? new Date(n.at).toLocaleString() : ''}</p>
                  </div>
                ))}
            </div>
            {canNote ? (
              <div className="px-5 py-3 border-t border-slate-100">
                <textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                  placeholder="Add a note for the preparer / bookkeeper…"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30" />
                <div className="flex justify-end mt-2">
                  <button data-testid="note-submit" onClick={addNote} disabled={!noteText.trim()}
                    className="bg-[#002FA7] text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40">Add Note</button>
                </div>
              </div>
            ) : <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-400">Only admins and accountants can add notes.</div>}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const SettingsForm = ({ settings, onSaved, readOnly = false }) => {
  const [f, setF] = useState({ entity_type: settings.entity_type, tax_form: settings.tax_form, legal_name: settings.legal_name || '', dba: settings.dba || '', address: settings.address || '' });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { const r = await ax.put('/tax/settings', f); onSaved(r.data); } finally { setSaving(false); } };
  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none disabled:bg-slate-50 disabled:text-slate-500';
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Business Tax Profile</h3>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-500">Entity Type
          <select data-testid="entity-type" disabled={readOnly} className={inp} value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })}>
            {settings.entity_types.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-500">Tax Form
          <select className={inp} disabled={readOnly} value={f.tax_form} onChange={(e) => setF({ ...f, tax_form: e.target.value })}>
            {settings.tax_forms.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
      </div>
      <label className="text-xs text-slate-500 block">Legal Business Name<input className={inp} disabled={readOnly} value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} /></label>
      <label className="text-xs text-slate-500 block">DBA<input className={inp} disabled={readOnly} value={f.dba} onChange={(e) => setF({ ...f, dba: e.target.value })} /></label>
      <label className="text-xs text-slate-500 block">Address<input className={inp} disabled={readOnly} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></label>
      {!readOnly && <button data-testid="save-settings" onClick={save} disabled={saving} className="bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40 flex items-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>}
    </div>
  );
};

const AddExpenseModal = ({ year, categories, onClose, onSaved }) => {
  const [f, setF] = useState({ expense_date: `${year}-01-01`, vendor_name: '', amount: '', category_id: '', payment_method: 'Company Credit Card', business_use_percentage: 100, description: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [vendors, setVendors] = useState([]);
  const [autofilled, setAutofilled] = useState(false);
  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none';
  useEffect(() => { ax.get('/tax/vendors').then((r) => setVendors(r.data || [])).catch(() => {}); }, []);
  const onVendorChange = (name) => {
    const nl = name.trim().toLowerCase();
    const match = nl ? vendors.find((v) => v.category_id && (v.name.toLowerCase().includes(nl) || nl.includes(v.name.toLowerCase()))) : null;
    if (match && !f.category_id) { setF((p) => ({ ...p, vendor_name: name, category_id: match.category_id })); setAutofilled(true); }
    else { setF((p) => ({ ...p, vendor_name: name })); if (!name) setAutofilled(false); }
  };
  const save = async () => {
    setErr('');
    if (!f.amount || Number(f.amount) <= 0) { setErr('Enter a valid amount.'); return; }
    setSaving(true);
    try {
      const cat = categories.find((c) => c.id === f.category_id);
      await ax.post('/tax/expenses', {
        ...f, amount: Number(f.amount),
        category_name: cat?.name, tax_report_category: cat?.tax_report_category,
        deductible_percentage: cat?.default_deductible_pct ?? 100,
      });
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not save.'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="add-expense-modal" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Add Expense</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        {err && <div className="mb-3 text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-500">Date<input type="date" data-testid="exp-date" className={inp} value={f.expense_date} onChange={(e) => setF({ ...f, expense_date: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Amount<input type="number" data-testid="exp-amount" className={inp} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0.00" /></label>
        </div>
        <label className="text-xs text-slate-500 block mt-3">Vendor<input data-testid="exp-vendor" className={inp} value={f.vendor_name} onChange={(e) => onVendorChange(e.target.value)} list="vendor-suggestions" placeholder="Start typing — known vendors auto-fill the category" />
          <datalist id="vendor-suggestions">{vendors.map((v) => <option key={v.id} value={v.name} />)}</datalist>
        </label>
        <label className="text-xs text-slate-500 block mt-3">Category {autofilled && f.category_id && <span data-testid="vendor-autofill-hint" className="text-[10px] text-[#002FA7] font-semibold">· auto-filled from vendor</span>}
          <select data-testid="exp-category" className={inp} value={f.category_id} onChange={(e) => { setF({ ...f, category_id: e.target.value }); setAutofilled(false); }}>
            <option value="">Uncategorized</option>
            {categories.filter((c) => c.enabled).map((c) => <option key={c.id} value={c.id}>{c.group} · {c.name}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="text-xs text-slate-500">Payment Method
            <select className={inp} value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}>
              {['Cash', 'Company Debit Card', 'Company Credit Card', 'ACH', 'Check', 'Fuel Card', 'Driver Reimbursement', 'Other'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-500">Business Use %<input type="number" className={inp} value={f.business_use_percentage} onChange={(e) => setF({ ...f, business_use_percentage: Number(e.target.value) })} /></label>
        </div>
        <label className="text-xs text-slate-500 block mt-3">Description<input className={inp} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="save-expense" onClick={save} disabled={saving} className="bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40 flex items-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Expense</button>
        </div>
      </div>
    </div>
  );
};

export default TaxesExpenses;
